import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { Status, ServiceCategory } from 'src/app/shared/enums/enums';
import {
  BillableServiceResponse,
  BillableServiceChargeResponse,
  CreateBillableServiceRequest,
  UpdateBillableServiceRequest,
  CreateBillableServiceChargeRequest,
  UpdateBillableServiceChargeRequest,
  mapServiceResponseToState
} from '../models';
import {
  PlanServiceState,
  SubscriptionPlanResponse
} from '../../subscription-plans/models';
import { environment } from 'src/app/environments/environment';

@Injectable({ providedIn: 'root' })
export class BillableServiceService {
  private readonly apiUrl = `${environment.companyApiUrl}/BillableServices`;
  private billableServices: BillableServiceResponse[] = [];
  private servicesSubject = new BehaviorSubject<BillableServiceResponse[]>([]);
  private dataLoaded = false;

  constructor(private http: HttpClient) {}

  /**
   * Refresh cache by clearing loaded flag to force reload
   */
  refreshCache(): void {
    this.dataLoaded = false;
  }

  /**
   * Load all billable services from API
   */
  loadData(status?: number): Observable<boolean> {
    if (this.dataLoaded) {
      return of(true);
    }
    let params = new HttpParams();
    if (status !== undefined) {
      params = params.set('status', status.toString());
    }
    return this.http.get<BillableServiceResponse[]>(this.apiUrl, { params }).pipe(
      tap(services => {
        this.billableServices = services;
        this.servicesSubject.next(this.billableServices);
        this.dataLoaded = true;
      }),
      map(() => true),
      catchError(error => {
        console.error('Error loading billable services:', error);
        this.billableServices = [];
        this.servicesSubject.next([]);
        return of(false);
      })
    );
  }

  /**
   * Get all billable services (with nested charges)
   */
  getBillableServices(): BillableServiceResponse[] {
    return this.billableServices;
  }

  /**
   * Fetch services with optional server-side search.
   * Bypasses cache when SearchTerm is provided.
   */
  fetchWithSearch(searchTerm?: string): Observable<BillableServiceResponse[]> {
    if (searchTerm && searchTerm.trim()) {
      const params = new HttpParams().set('SearchTerm', searchTerm.trim());
      return this.http.get<BillableServiceResponse[]>(this.apiUrl, { params }).pipe(
        catchError(error => {
          console.error('Error searching billable services:', error);
          return of([]);
        })
      );
    }
    return this.loadData().pipe(map(() => this.billableServices));
  }

  /**
   * Get app access services (category = AppAccess)
   */
  getAppAccessServices(): BillableServiceResponse[] {
    return this.billableServices.filter(s => s.category === ServiceCategory.AppAccess);
  }

  /**
   * Get addon services (category = 'AddOn')
   */
  getAddonServices(): BillableServiceResponse[] {
    return this.billableServices.filter(s => s.category === ServiceCategory.AddOn);
  }

  /**
   * Get billable services with query parameters (for add-ons selection)
   * @param appIds - Comma-separated app IDs
   * @param status - Status filter (1 = Active, 2 = Inactive, etc.)
   * @param category - ServiceCategory filter (2 = AddOn)
   */
  getBillableServicesWithFilters(appIds?: string, status?: number, category?: ServiceCategory): Observable<BillableServiceResponse[]> {
    let params = new HttpParams();

    if (appIds) {
      params = params.set('appIds', appIds);
    }
    if (status !== undefined) {
      params = params.set('status', status.toString());
    }
    if (category !== undefined) {
      params = params.set('category', category.toString());
    }

    return this.http.get<BillableServiceResponse[]>(this.apiUrl, { params }).pipe(
      catchError(error => {
        console.error('Error fetching billable services with filters:', error);
        return of([]);
      })
    );
  }

  /**
   * Get charges for a specific service (already nested in service)
   */
  getChargesForService(serviceId: number): BillableServiceChargeResponse[] {
    const service = this.billableServices.find(s => s.id === serviceId);
    return service?.billableServiceCharges || [];
  }

  /**
   * Get a specific service by ID - always fetches fresh data from API
   */
  getServiceById(id: number): Observable<BillableServiceResponse | undefined> {
    return this.http.get<BillableServiceResponse>(`${this.apiUrl}/${id}`).pipe(
      tap(service => {
        // Update cache with fresh data
        const index = this.billableServices.findIndex(s => s.id === id);
        if (index !== -1) {
          this.billableServices[index] = service;
        } else {
          this.billableServices.push(service);
        }
        this.servicesSubject.next(this.billableServices);
      }),
      catchError(error => {
        console.error('Error fetching service by id:', error);
        return of(undefined);
      })
    );
  }



  /**
   * Build service state for plan builder
   */
  buildServiceState(service: BillableServiceResponse): PlanServiceState {
    return mapServiceResponseToState(service);
  }

  /**
   * Create a new billable service
   */
  createService(request: CreateBillableServiceRequest): Observable<BillableServiceResponse> {
    return this.http.post<BillableServiceResponse>(this.apiUrl, request).pipe(
      tap(newService => {
        this.refreshCache();
      }),
      catchError(error => {
        console.error('Error creating service:', error);
        throw error;
      })
    );
  }

  /**
   * Update an existing billable service
   */
  updateService(id: number, request: UpdateBillableServiceRequest): Observable<BillableServiceResponse | null> {
    return this.http.put<BillableServiceResponse>(`${this.apiUrl}/${id}`, request).pipe(
      tap(updatedService => {
        this.refreshCache();
      }),
      catchError(error => {
        console.error('Error updating service:', error);
        throw error;
      })
    );
  }

  /**
   * Create a new charge for a service
   */
  createCharge(
    serviceId: number,
    request: CreateBillableServiceChargeRequest
  ): Observable<BillableServiceChargeResponse | null> {
    return this.http.post<BillableServiceChargeResponse>(`${this.apiUrl}/${serviceId}/charges`, request).pipe(
      tap(newCharge => {
        this.refreshCache();
      }),
      catchError(error => {
        console.error('Error creating charge:', error);
        throw error;
      })
    );
  }

  /**
   * Update an existing charge
   */
  updateCharge(
    serviceId: number,
    chargeId: number,
    request: UpdateBillableServiceChargeRequest
  ): Observable<BillableServiceChargeResponse | null> {
    return this.http.put<BillableServiceChargeResponse>(`${this.apiUrl}/${serviceId}/charges/${chargeId}`, request).pipe(
      tap(updatedCharge => {
        this.refreshCache();
      }),
      catchError(error => {
        console.error('Error updating charge:', error);
        throw error;
      })
    );
  }

  /**
   * Delete a billable service
   */
  deleteService(id: number): Observable<boolean> {
    return this.http.delete(`${this.apiUrl}/${id}`).pipe(
      map(() => {
        this.refreshCache();
        return true;
      }),
      catchError(error => {
        console.error('Error deleting service:', error);
        return of(false);
      })
    );
  }

  /**
   * Delete a charge from a service
   */
  deleteCharge(serviceId: number, chargeId: number): Observable<boolean> {
    return this.http.delete(`${this.apiUrl}/${serviceId}/charges/${chargeId}`).pipe(
      map(() => {
        this.refreshCache();
        return true;
      }),
      catchError(error => {
        console.error('Error deleting charge:', error);
        return of(false);
      })
    );
  }

  /**
   * Change service status to a specific value
   */
  changeStatus(id: number, newStatus: Status): Observable<boolean> {
    const statusRequest = { status: newStatus };

    return this.http.put<BillableServiceResponse>(`${this.apiUrl}/${id}/changestatus`, statusRequest).pipe(
      map(updatedService => {
        this.refreshCache();
        return true;
      }),
      catchError(error => {
        console.error('Error changing service status:', error);
        throw error;
      })
    );
  }

  /**
   * Toggle service status between Active and Inactive
   */
  toggleServiceStatus(id: number): Observable<boolean> {
    const services = this.servicesSubject.value;
    const service = services.find(s => s.id === id);
    if (!service) return of(false);

    const newStatus = service.status === Status.Active ? Status.Inactive : Status.Active;
    const statusRequest = { status: newStatus };

    return this.http.put<BillableServiceResponse>(`${this.apiUrl}/${id}/changestatus`, statusRequest).pipe(
      map(updatedService => {
        this.refreshCache();
        return true;
      }),
      catchError(error => {
        console.error('Error toggling service status:', error);
        return of(false);
      })
    );
  }
}
